import { Inject, Injectable } from '@angular/core';
import { LoadRequest, SortDirection, SortSetting, TableDataService } from '@vendasta/va-filter2-table';
import { delay, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AGIDTOKEN,
  AppleSourceID,
  BingSourceID,
  FacebookSourceID,
  GoogleSourceID,
  InstagramSourceID,
  SyncData,
  XSourceID,
} from '../../local-seo';
import { ListingSyncService } from '../listing-sync.service';
import { SyncStatus } from '@vendasta/listing-products';

export const SITE_TYPE_MAPPING = new Map<string, string>([
  ['directory-site', `LISTING_SYNC.LSP_ADDON.COLUMNS.SITE_TYPE.TYPES.DIRECTORY`],
  ['map', `LISTING_SYNC.LSP_ADDON.COLUMNS.SITE_TYPE.TYPES.MAP`],
  ['social-site', `LISTING_SYNC.LSP_ADDON.COLUMNS.SITE_TYPE.TYPES.SOCIAL`],
  ['internet-data-provider', `LISTING_SYNC.LSP_ADDON.COLUMNS.SITE_TYPE.TYPES.AGGREGATOR`],
  ['app', `LISTING_SYNC.LSP_ADDON.COLUMNS.SITE_TYPE.TYPES.APP`],
  ['gps', `LISTING_SYNC.LSP_ADDON.COLUMNS.SITE_TYPE.TYPES.GPS`],
  ['review-site', `LISTING_SYNC.LSP_ADDON.COLUMNS.SITE_TYPE.TYPES.REVIEW`],
  ['search-engine', `LISTING_SYNC.LSP_ADDON.COLUMNS.SITE_TYPE.TYPES.SEARCH_ENGINE`],
]);

// mirrors getAccuracyText except for sorting with a private enum
function sortAccuracy(a: SyncData, b: SyncData): number {
  const aCode = a?.accuracy?.status || 0;
  const bCode = b?.accuracy?.status || 0;
  return aCode > bCode ? 1 : bCode > aCode ? -1 : 0;
}

export function getLastUpdated(lastUpdated: Date): string {
  if (lastUpdated === undefined) {
    return 'Never';
  }
  const lastUpdatedDate = new Date(lastUpdated);
  const differenceInTime = new Date().getTime() - lastUpdatedDate.getTime();
  const differenceInDays = Math.floor(differenceInTime / (1000 * 3600 * 24));

  if (differenceInDays === 0) {
    return 'Just updated';
  }

  return differenceInDays + ' days ago';
}

function sortSyncStatus(a: SyncData, b: SyncData): number {
  const aCode = a.syncStatus || 0;
  const bCode = b.syncStatus || 0;
  return aCode > bCode ? 1 : bCode > aCode ? -1 : 0;
}

function _columnTypeSpecificComparitor(field: string, a: SyncData, b: SyncData): number {
  switch (field) {
    // Date
    case 'lastUpdated': {
      const x = a.lastUpdatedDate;
      const y = b.lastUpdatedDate;

      // handle when date is 'never'
      if (x !== undefined && y === undefined) {
        return 1;
      } else if (x === undefined && y !== undefined) {
        return -1;
      } else if (x === undefined && y === undefined) {
        return 0;
      }

      return x > y ? 1 : y > x ? -1 : 0;
    }
    // Strings
    case 'name': {
      // normalize to lower case
      return a[field].localeCompare(b[field]);
    }
    // Numbers
    case 'score':
    case 'maxScore':
      return a[field] > b[field] ? 1 : b[field] > a[field] ? -1 : 0;
    // Enums
    case 'accuracyText':
      return sortAccuracy(a, b);
    case 'syncText':
      return sortSyncStatus(a, b);
    case 'type':
      return a.sourceType > b.sourceType ? 1 : b.sourceType > a.sourceType ? -1 : 0;
    default:
      // unknown column type, defaulting sort to number sort
      return a[field] > b[field] ? 1 : b[field] > a[field] ? -1 : 0;
  }

  return 0;
}

function _columnSort(sortOptions: SortSetting[], sortOptionIndex: number, a: SyncData, b: SyncData): number {
  const field = sortOptions[sortOptionIndex].field;
  const ascend = sortOptions[sortOptionIndex].direction === SortDirection.SORT_DIRECTION_ASCENDING;
  const result = _columnTypeSpecificComparitor(field, a, b);

  // If the sort results in a tie then progress to the next highest priority sort
  if (result === 0) {
    if (sortOptionIndex === sortOptions.length - 1) {
      return 0;
    }
    return _columnSort(sortOptions, sortOptionIndex + 1, a, b);
  }
  return ascend ? result : result * -1;
}

function columnSortEntry(sortOptions: SortSetting[], a: SyncData, b: SyncData): number {
  if (sortOptions.length === 0) {
    return 0;
  }
  return _columnSort(sortOptions, 0, a, b);
}

function applyDefaultSortOptions(sortOpts: SortSetting[]): void {
  const sortMaxScoreOpt: SortSetting = {
    ascendingLabel: 'LISTING_SYNC.TABLE_LABEL.SORT_NUMBER_ASC',
    descendingLabel: 'LISTING_SYNC.TABLE_LABEL.SORT_NUMBER_DSC',
    direction: SortDirection.SORT_DIRECTION_DESCENDING,
    displayName: 'LISTING_SYNC.COLUMN_HEADERS.MAX_SCORE',
    field: 'maxScore',
    id: 'scoreMax',
  };

  if (sortOpts.length === 0) {
    sortOpts.push(sortMaxScoreOpt);
  }
}

@Injectable({
  providedIn: 'root',
})
export class ListingSyncTableService implements TableDataService<SyncData> {
  totalResults$: Observable<number>;
  // lspEnabled = false;
  private excludedSources = [
    AppleSourceID,
    FacebookSourceID,
    GoogleSourceID,
    InstagramSourceID,
    XSourceID,
    BingSourceID,
  ];

  constructor(
    @Inject(AGIDTOKEN) private accountGroupId$: Observable<string>,
    private listingSyncService: ListingSyncService,
  ) {}

  load(request: LoadRequest): Observable<SyncData[]> {
    return this.listingSyncService.syncData$.pipe(
      map((sources: SyncData[]) => {
        sources = sources.filter((value) => {
          return !this.excludedSources.includes(+value.sourceId);
        });

        if (request.filters['search']) {
          sources = sources.filter((value) => {
            const query: string = request.filters['search'];
            return value.name.toLowerCase().startsWith(query.toLowerCase());
          });
        }

        if (request.filters['syncing_available']) {
          sources = sources.filter((value) => {
            return value.syncStatus !== SyncStatus.SYNC_STATUS_UNAVAILABLE;
          });
        }

        if (request.filters['manual_update_only']) {
          sources = sources.filter((value) => {
            return value.syncStatus === SyncStatus.SYNC_STATUS_UNAVAILABLE;
          });
        }

        const sortOpts = [...request.sortingOptions];
        applyDefaultSortOptions(sortOpts);

        const result = this.sortTableRows(sources, sortOpts);
        return result;
      }),
      delay(0),
    );
  }

  sortTableRows(data: SyncData[], options?: SortSetting[]): SyncData[] {
    const cmpGenerator = function (): (a: SyncData, b: SyncData) => number {
      return (a: SyncData, b: SyncData): number => {
        return columnSortEntry(options, a, b);
      };
    };

    data = data.sort(cmpGenerator());

    return data;
  }
}
